"use client";

import { FormEvent, useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";

interface LoginResponse {
  user?: {
    email: string;
  };
  error?: string;
}

interface SetupStatus {
  setupRequired: boolean;
  registrationCreatesAdmin: boolean;
}

export default function LoginForm({ initialError }: { initialError: string }) {
  const [error, setError] = useState(initialError);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadSetupStatus() {
      try {
        const response = await fetch("/api/auth/setup-status", {
          credentials: "same-origin",
          cache: "no-store",
        });
        if (!response.ok) return;

        const status = (await response.json()) as SetupStatus;
        if (cancelled) return;

        if (status.registrationCreatesAdmin) {
          window.location.replace("/register");
          return;
        }

        setSetupRequired(status.setupRequired);
      } catch {
        // Login remains usable if setup discovery is temporarily unavailable.
      }
    }

    void loadSetupStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    const formData = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          email: formData.get("email"),
          password: formData.get("password"),
        }),
      });
      const result = (await response.json()) as LoginResponse;

      if (!response.ok || !result.user) {
        setError(result.error ?? "La connexion a échoué.");
        return;
      }

      window.location.assign("/dashboard");
    } catch {
      setError("Le service est momentanément indisponible.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-emerald-950 px-4 py-4 sm:py-6">
      <section className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl sm:p-7">
        <div className="mb-5 text-center">
          <div className="relative mx-auto mb-2 h-24 w-20 overflow-hidden">
            <Image
              alt="Logo Sylvia"
              className="object-contain"
              fill
              priority
              sizes="80px"
              src="/images/sylvia-logo.jpeg"
            />
          </div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-700">
            Sylvia
          </p>
          <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-slate-950">
            Espace client
          </h1>
          <p className="mt-1.5 text-sm leading-5 text-slate-600">
            Connectez-vous avec votre adresse email et votre mot de passe.
          </p>
        </div>

        {setupRequired ? (
          <p className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-900">
            Aucun administrateur n’est encore configuré. Connectez-vous avec le
            premier compte créé pour finaliser automatiquement l’initialisation.
          </p>
        ) : null}

        <form
          action="/api/auth/login"
          method="post"
          className="space-y-4"
          onSubmit={handleSubmit}
        >
          <div>
            <label
              className="mb-2 block text-sm font-semibold text-slate-800"
              htmlFor="email"
            >
              Adresse email
            </label>
            <input
              autoComplete="email"
              className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-slate-950 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
              id="email"
              name="email"
              required
              type="email"
            />
          </div>

          <div>
            <label
              className="mb-2 block text-sm font-semibold text-slate-800"
              htmlFor="password"
            >
              Mot de passe
            </label>
            <input
              autoComplete="current-password"
              className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-slate-950 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
              id="password"
              name="password"
              required
              type="password"
            />
          </div>

          {error ? (
            <p
              aria-live="polite"
              className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </p>
          ) : null}

          <button
            className="w-full rounded-xl bg-emerald-700 px-4 py-2.5 font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-slate-600">
          Pas encore de compte ?{" "}
          <Link
            className="font-semibold text-emerald-700 hover:text-emerald-800"
            href="/register"
          >
            Créer un compte
          </Link>
        </p>
      </section>
    </main>
  );
}
